import React, { useState, useEffect } from 'react';
import { useOffline } from '../../contexts/OfflineContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCloud, FiCloudOff, FiRefreshCw, FiCheckCircle } = FiIcons;

/**
 * LiveSyncStatusPill
 * Real-time indicators of connection & outbox sync queue status
 */
export const LiveSyncStatusPill = ({ pendingSyncCount = 0, onTriggerSync }) => {
  const { isOnline } = useOffline();
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);

  const handleManualSync = async () => {
    if (!isOnline || syncing) return;
    setSyncing(true);
    try {
      if (onTriggerSync) {
        await onTriggerSync();
      } else {
        // simulate standard non-blocking sync ping
        await new Promise((r) => setTimeout(r, 800));
      }
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3000);
    } catch (e) {
      console.warn('Sync failed:', e);
    } finally {
      setSyncing(false);
    }
  };

  if (!isOnline) {
    return (
      <div 
        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20"
        title="Offline Mode: All changes are saved locally to Dexie IndexedDB and will sync when reconnected."
      >
        <SafeIcon icon={FiCloudOff} className="w-3.5 h-3.5 animate-pulse text-amber-500" />
        <span className="hidden sm:inline">Offline Mode</span>
        {pendingSyncCount > 0 && (
          <span className="px-1.5 py-0.2 bg-amber-500 text-white text-[10px] font-bold rounded-full">
            {pendingSyncCount}
          </span>
        )}
      </div>
    );
  }

  if (syncing) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
        <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 animate-spin text-blue-500" />
        <span className="hidden sm:inline">Syncing Cloud...</span>
      </div>
    );
  }

  if (justSynced) {
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
        <SafeIcon icon={FiCheckCircle} className="w-3.5 h-3.5 text-emerald-500" />
        <span className="hidden sm:inline">Synced</span>
      </div>
    );
  }

  return (
    <button
      onClick={handleManualSync}
      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 transition-colors"
      title="Click to synchronize local Dexie outbox with PocketBase"
    >
      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
      <SafeIcon icon={FiCloud} className="w-3.5 h-3.5 text-emerald-500" />
      <span className="hidden sm:inline">Cloud Synced</span>
      {pendingSyncCount > 0 && (
        <span className="px-1.5 py-0.2 bg-blue-600 text-white text-[10px] font-bold rounded-full">
          {pendingSyncCount}
        </span>
      )}
    </button>
  );
};

export default LiveSyncStatusPill;
